/** @file Assembles a PNG: signature, IHDR, extra chunks, fixed 64 KiB IDATs from deflate output, IEND. */
import { openDeflate } from './openDeflate';
import { pngChunk } from '../pngChunk';

/** IDAT payload size: fixed so the chunk split never depends on stream scheduling (spike S3). */
const IDAT_BYTES = 65536;
const SIGNATURE = Uint8Array.of(137, 80, 78, 71, 13, 10, 26, 10);

export interface PngStreamHeader {
  width: number;
  height: number;
  /** 6 = RGBA8, 3 = palette (8-bit indices). */
  colorType: 3 | 6;
  /** Finished chunks written between IHDR and the first IDAT (PLTE, tRNS). */
  before: Uint8Array[];
}

export interface PngStream {
  /** Deflates filtered scanlines (one filter byte + the row's bytes, per row). */
  write(scanlines: Uint8Array): Promise<void>;
  /** Flushes deflate and returns the complete file. */
  finish(): Promise<Uint8Array>;
}

/** Starts an 8-bit, non-interlaced PNG; only IHDR, `before`, IDAT and IEND are written (no colour chunks). */
export function createPngStream(header: PngStreamHeader): PngStream {
  const parts: Uint8Array[] = [SIGNATURE, pngChunk('IHDR', ihdr(header)), ...header.before];
  const pending = new Uint8Array(IDAT_BYTES);
  let fill = 0;
  const deflate = openDeflate((bytes) => {
    for (let offset = 0; offset < bytes.length; ) {
      const n = Math.min(IDAT_BYTES - fill, bytes.length - offset);
      pending.set(bytes.subarray(offset, offset + n), fill);
      fill += n;
      offset += n;
      if (fill === IDAT_BYTES) {
        parts.push(pngChunk('IDAT', pending));
        fill = 0;
      }
    }
  });
  return {
    write: (scanlines) => deflate.write(scanlines),
    finish: async () => {
      await deflate.close();
      if (fill > 0) parts.push(pngChunk('IDAT', pending.subarray(0, fill)));
      parts.push(pngChunk('IEND', new Uint8Array(0)));
      return concat(parts);
    },
  };
}

/** The 13-byte IHDR payload: width, height, bit depth 8, colour type, deflate, adaptive filters, no interlace. */
function ihdr({ width, height, colorType }: PngStreamHeader): Uint8Array {
  const out = new Uint8Array(13);
  const view = new DataView(out.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  out[8] = 8;
  out[9] = colorType;
  return out;
}

/** Joins byte arrays into one. */
function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
